import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle2, XCircle, Mail } from 'lucide-react';
import { fetchEmailStats, selectEmailStats } from '../../store/slices/preparativosSlice';
import { useToast } from '../Toast/ToastContainer';
import './EmailResponseCounter.css';

const EmailResponseCounter = () => {
  const dispatch = useDispatch();
  const emailStats = useSelector(selectEmailStats);
  const toast = useToast();
  const prevStatsRef = useRef({ successCount: 0, errorCount: 0 });

  // Fetch stats on mount and every 30 seconds
  useEffect(() => {
    // Initial fetch
    dispatch(fetchEmailStats());

    // Set up polling interval (30 seconds)
    const interval = setInterval(() => {
      dispatch(fetchEmailStats());
    }, 30000); // 30 seconds

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [dispatch]);

  // Show notification when stats change
  useEffect(() => {
    const prevStats = prevStatsRef.current;
    
    console.log('[EmailResponseCounter] Stats update:', {
      prev: prevStats,
      current: { successCount: emailStats.successCount, errorCount: emailStats.errorCount },
      lastEmail: emailStats.lastEmail
    });
    
    // Skip on initial load or if no previous data
    if (prevStats.successCount === 0 && prevStats.errorCount === 0 && 
        (emailStats.successCount > 0 || emailStats.errorCount > 0)) {
      console.log('[EmailResponseCounter] Initial load - skipping notification');
      prevStatsRef.current = {
        successCount: emailStats.successCount,
        errorCount: emailStats.errorCount
      };
      return;
    }

    // Helper function to get step name in Spanish
    const getStepName = (stepType) => {
      const stepNames = {
        'solicitud_embarque': 'Solicitud de Embarque',
        'coordinar_transporte': 'Coordinar Transporte',
        'solicitud_dus': 'Solicitud de DUS'
      };
      return stepNames[stepType] || stepType;
    };

    // Check if success count increased
    if (emailStats.successCount > prevStats.successCount) {
      const newSuccesses = emailStats.successCount - prevStats.successCount;
      let message = `✅ ${newSuccesses} correo${newSuccesses > 1 ? 's' : ''} procesado${newSuccesses > 1 ? 's' : ''} exitosamente`;
      
      // Add details if lastEmail is available and it's a success
      if (emailStats.lastEmail && emailStats.lastEmail.status === 'success') {
        const stepName = getStepName(emailStats.lastEmail.step_type);
        message += `\n${stepName} - Temporada ${emailStats.lastEmail.temporada}, Semana ${emailStats.lastEmail.semana}`;
      }
      
      console.log('[EmailResponseCounter] Showing success notification:', message);
      toast.success(message, 5000);
    }

    // Check if error count increased
    if (emailStats.errorCount > prevStats.errorCount) {
      const newErrors = emailStats.errorCount - prevStats.errorCount;
      let message = `❌ ${newErrors} error${newErrors > 1 ? 'es' : ''} al procesar correo${newErrors > 1 ? 's' : ''}`;
      
      // Add details if lastEmail is available and it's an error
      if (emailStats.lastEmail && emailStats.lastEmail.status === 'error') {
        const stepName = getStepName(emailStats.lastEmail.step_type);
        message += `\n${stepName} - Temporada ${emailStats.lastEmail.temporada}, Semana ${emailStats.lastEmail.semana}`;
      }
      
      console.log('[EmailResponseCounter] Showing error notification:', message);
      toast.error(message, 5000);
    }

    // Update previous stats
    prevStatsRef.current = {
      successCount: emailStats.successCount,
      errorCount: emailStats.errorCount
    };
  }, [emailStats.successCount, emailStats.errorCount, emailStats.lastEmail, toast]);

  return (
    <div className="email-response-counter" title="Respuestas de correos recibidas hoy">
      <div className="email-counter-icon">
        <Mail size={18} />
      </div>
      
      <span className="email-counter-label">Correos:</span>
      
      <div className="email-counter-stats">
        <div className="email-stat success">
          <CheckCircle2 size={16} />
          <span>{emailStats.successCount || 0}</span>
        </div>
        
        <div className="email-stat error">
          <XCircle size={16} />
          <span>{emailStats.errorCount || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default EmailResponseCounter;
